import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable, Subject } from "rxjs";
import { QuestionDetail } from "../modules/pages/analysis/models/question-detail";

import inglese from "../../assets/i18n/json/en-GB.json";
import { NetworkFilterValues } from "../shared/filters/network-form";
import { AdminModuleListService } from "../modules/pages/admin-module/admin-module-service";

export interface Navbar {
  children: any[];
  disable: string;
  displayName: string;
  iconName: string;
  idItem: string;
  itemType: string;
  menuScope: any[];
  name: string;
  route: string;
}

export interface TouchpointChange {
  previousSection: string;
  currentSection: string;
  fromDashboard?: boolean;
}

export const TOUCHPOINT_FILTER_PAYLOAD_STORAGE_KEY =
  "touchpointFilterPayload";

@Injectable({
  providedIn: "root",
})
export class ApplicationService {
  private readonly lastTouchpointStorageKey = "lastTouchpointId";

  public filterSettingSubject: BehaviorSubject<any>;
  public filterSetting: Observable<any>;



  public openEReputationSubject: BehaviorSubject<any>;
  public openEReputation: Observable<any>;

  public openImporterSubject: BehaviorSubject<any>;
  public openImporter: Observable<any>;

  public boxesEReputationSubject: BehaviorSubject<any>;
  public boxesEReputation: Observable<any>;



  public navBarSelectSubject: BehaviorSubject<Navbar>;
  public navBarSelect: Observable<Navbar>;
  private readonly touchpointChangeSubject = new Subject<TouchpointChange>();
  public readonly touchpointChange = this.touchpointChangeSubject.asObservable();
  private readonly filterSessionResetSubject = new Subject<void>();
  public readonly filterSessionReset =
    this.filterSessionResetSubject.asObservable();
  private lastTouchpointId = "";

  public menuItemSelectSubject: BehaviorSubject<any>;
  public menuItemSelect: Observable<any>;

  public translationListSelectSubject: BehaviorSubject<any>;
  public translationListSelect: Observable<any>;

  public languageSelectSubject: BehaviorSubject<string>;
  public languageSelect: Observable<string>;

  public previousCurrentRouteSubject: BehaviorSubject<{ previous: string, current: string }>;
  public previousCurrentRoute: Observable<{ previous: string, current: string }>;


  public inputFormNetworkSubject = new BehaviorSubject<NetworkFilterValues | undefined>(undefined);
  public inputFromNetwork: Observable<NetworkFilterValues | undefined>;

  public selectedBrandsSubject = new BehaviorSubject<string[]>([]);
  public selectedBrands$ = this.selectedBrandsSubject.asObservable();

  public setSelectedBrands(brands: string[]) {
    this.selectedBrandsSubject.next(brands);
  }

  public get selectedBrandsValue(): string[] {
    return this.selectedBrandsSubject.getValue();
  }



  constructor(private http: HttpClient, private adminModuleListService: AdminModuleListService) {

    this.previousCurrentRouteSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("previousCurrentRoute")!)
    );
    this.previousCurrentRoute = this.previousCurrentRouteSubject.asObservable();


    this.filterSettingSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("filterSetting")!)
    );
    this.filterSetting = this.filterSettingSubject.asObservable();

    this.openEReputationSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("e-reputation-open")!)
    );
    this.openEReputation = this.openEReputationSubject.asObservable();


    this.openImporterSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("importer-open")!)
    );
    this.openImporter = this.openImporterSubject.asObservable();


    this.boxesEReputationSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("e-reputation-check-all")!)
    );
    this.boxesEReputation = this.boxesEReputationSubject.asObservable();


    const storedNavBar = this.readStoredNavbar();
    this.navBarSelectSubject = new BehaviorSubject<Navbar>(storedNavBar as Navbar);
    this.lastTouchpointId = this.isTouchpoint(storedNavBar)
      ? storedNavBar.idItem
      : this.readStoredString(this.lastTouchpointStorageKey) ||
        this.readStoredSection();
    this.adminModuleListService.distributionType = undefined;
    this.navBarSelect = this.navBarSelectSubject.asObservable();

    this.menuItemSelectSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("menuItemNav")!)
    );
    this.menuItemSelect = this.navBarSelectSubject.asObservable();

    this.translationListSelectSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("translationList")!)
    );
    this.translationListSelect = this.translationListSelectSubject.asObservable();

    this.inputFormNetworkSubject = new BehaviorSubject<NetworkFilterValues | undefined>(
      this.readStoredValue<NetworkFilterValues>("inputFormNetwork")
    );
    this.inputFromNetwork = this.inputFormNetworkSubject.asObservable();

    let storageLang = localStorage.getItem("language");
    let language = storageLang ? JSON.parse(storageLang) : "en-GB";
    this.languageSelectSubject = new BehaviorSubject<string>(language);
    this.languageSelect = this.languageSelectSubject.asObservable();
  }


  setFilters(filters: NetworkFilterValues | undefined) {
    const rawFilters = filters;
    localStorage.setItem("inputFormNetwork", JSON.stringify(rawFilters));
    this.inputFormNetworkSubject.next(rawFilters);
  }

  // Metodo per ottenere il valore attuale dei filtri
  getFilters(): NetworkFilterValues | undefined {
    return this.inputFormNetworkSubject.getValue();
  }

  // Metodo per resettare i filtri a `undefined`
  clearFilters() {
    localStorage.removeItem("inputFormNetwork");
    this.inputFormNetworkSubject.next(undefined);
  }

  /** Clears state that may be preserved only inside one authenticated session. */
  resetFilterSessionState(): void {
    this.clearFilters();
    this.lastTouchpointId = "";
    localStorage.removeItem(this.lastTouchpointStorageKey);
    // TouchpointFilterStateService may not exist yet when a login starts a new
    // authenticated session, so clear its persisted canonical snapshot here as
    // part of the account boundary as well as through filterSessionReset.
    sessionStorage.removeItem(TOUCHPOINT_FILTER_PAYLOAD_STORAGE_KEY);
    this.filterSessionResetSubject.next();
    localStorage.removeItem("navBar");
    this.navBarSelectSubject.next(null as unknown as Navbar);
    this.selectedBrandsSubject.next([]);
  }



  public setBoxesEReputationSubject(eReputation: any) {
    localStorage.setItem("e-reputation-check-all", JSON.stringify(eReputation));
    this.boxesEReputationSubject.next(<any>eReputation);
  }
  public get boxesEReputationValue() {
    return this.boxesEReputationSubject.value;
  }

  public setOpenEReputation(eReputation: any) {
    localStorage.setItem("e-reputation-open", JSON.stringify(eReputation));
    this.openEReputationSubject.next(<any>eReputation);
  }
  public get openEReputationValue() {
    return this.openEReputationSubject.value;
  }
  public setOpenImporter(importer: any) {
    localStorage.setItem("importer-open", JSON.stringify(importer));
    this.openImporterSubject.next(<any>importer);
  }
  public get openImporterValue() {
    return this.openImporterSubject.value;
  }
  public setFilterSetting(filterSetting: any) {
    localStorage.setItem("filterSetting", JSON.stringify(filterSetting));
    this.filterSettingSubject.next(<any>filterSetting);
  }
  public get filterSettingValue() {
    return this.filterSettingSubject.value;
  }

  public setPreviousCurrentRoute(previousCurrentRoute: any) {
    localStorage.setItem("previousCurrentRoute", JSON.stringify(previousCurrentRoute));
    this.previousCurrentRouteSubject.next(<any>previousCurrentRoute);
  }
  public get previousCurrentRouteValue() {
    return this.previousCurrentRouteSubject.value;
  }


  public setNavBarValue(navBar: any) {
    const normalizedNavBar = navBar
      ? {
          ...navBar,
          idItem: navBar.idItem?.toUpperCase().includes("CEC")
            ? navBar.idItem.toUpperCase()
            : navBar.idItem,
        }
      : navBar;
    const nextTouchpointId = this.isTouchpoint(normalizedNavBar)
      ? normalizedNavBar.idItem
      : "";
    const currentRoute = (this.previousCurrentRouteValue?.current || "").split(
      /[?#]/,
      1
    )[0];
    const fromDashboard =
      !this.isTouchpoint(this.navBarSelectValue) &&
      (currentRoute === "/dashboard" || currentRoute.startsWith("/dashboard/"));

    if (
      nextTouchpointId &&
      (fromDashboard || (this.lastTouchpointId && nextTouchpointId !== this.lastTouchpointId))
    ) {
      // Publish the transition first so the old payload is invalidated before
      // consumers combine it with the newly selected touchpoint.
      this.touchpointChangeSubject.next({
        previousSection: this.lastTouchpointId,
        currentSection: nextTouchpointId,
        ...(fromDashboard ? { fromDashboard: true } : {}),
      });
    }

    // Dashboard publishes null. Keep the last real touchpoint so a subsequent
    // dashboard -> touchpoint navigation is still recognized as a change.
    if (nextTouchpointId) {
      this.lastTouchpointId = nextTouchpointId;
      localStorage.setItem(
        this.lastTouchpointStorageKey,
        JSON.stringify(nextTouchpointId)
      );
    }

    localStorage.setItem("navBar", JSON.stringify(normalizedNavBar));
    this.adminModuleListService.distributionType = undefined;
    this.navBarSelectSubject.next(<any>normalizedNavBar);
  }

  /** Restores the previous selection after a failed transition without emitting a new one. */
  restoreTouchpointSelection(
    navBar: Navbar | null,
    lastTouchpointId = navBar?.idItem || this.lastTouchpointId
  ): void {
    this.lastTouchpointId = lastTouchpointId;
    if (lastTouchpointId) {
      localStorage.setItem(
        this.lastTouchpointStorageKey,
        JSON.stringify(lastTouchpointId)
      );
    } else {
      localStorage.removeItem(this.lastTouchpointStorageKey);
    }
    localStorage.setItem("navBar", JSON.stringify(navBar));
    this.navBarSelectSubject.next(navBar as Navbar);
  }

  public get navBarSelectValue() {
    return this.navBarSelectSubject.value;
  }

  public get lastTouchpointValue(): string {
    return this.lastTouchpointId;
  }

  private readStoredSection(): string {
    const section = this.readStoredValue<unknown>("settingsPayload");
    if (
      !this.isRecord(section) ||
      !this.isRecord(section["payload"]) ||
      !this.isRecord(section["payload"]["question"])
    ) {
      return "";
    }

    const storedSection = section["payload"]["question"]["section"];
    return typeof storedSection === "string" ? storedSection : "";
  }

  private isTouchpoint(navBar?: Navbar | null): navBar is Navbar {
    return (
      typeof navBar?.itemType === "string" &&
      navBar.itemType.toUpperCase() === "JOURNEY" &&
      typeof navBar.idItem === "string" &&
      !!navBar.idItem
    );
  }

  private readStoredNavbar(): Navbar | undefined {
    const storedNavBar = this.readStoredValue<unknown>("navBar");
    if (storedNavBar == null) {
      return undefined;
    }
    if (
      !this.isRecord(storedNavBar) ||
      typeof storedNavBar["itemType"] !== "string" ||
      typeof storedNavBar["idItem"] !== "string" ||
      typeof storedNavBar["name"] !== "string"
    ) {
      localStorage.removeItem("navBar");
      return undefined;
    }

    return storedNavBar as unknown as Navbar;
  }

  private readStoredString(key: string): string {
    const storedValue = this.readStoredValue<unknown>(key);
    if (typeof storedValue === "string") {
      return storedValue;
    }
    if (storedValue !== undefined) {
      localStorage.removeItem(key);
    }
    return "";
  }

  private readStoredValue<T>(key: string): T | undefined {
    const storedValue = localStorage.getItem(key);
    if (!storedValue || storedValue === "undefined") {
      if (storedValue === "undefined") {
        localStorage.removeItem(key);
      }
      return undefined;
    }

    try {
      return JSON.parse(storedValue) as T;
    } catch {
      localStorage.removeItem(key);
      return undefined;
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  public setMenuItemValue(menuItem: any) {
    localStorage.setItem("menuItemNav", JSON.stringify(menuItem));
    this.adminModuleListService.distributionType = undefined;
    this.menuItemSelectSubject.next(<any>menuItem);
  }
  public get menuIteSelectValue() {
    return this.menuItemSelectSubject.value;
  }

  public setTranslationListValue(settingsUser: any) {
    localStorage.setItem("translationList", JSON.stringify(settingsUser));
    this.translationListSelectSubject.next(<any>settingsUser);
  }
  public get translationListSelectValue() {
    return this.translationListSelectSubject.value;
  }

  public setLanguageValue(language: string) {
    localStorage.setItem("language", JSON.stringify(language));
    this.languageSelectSubject.next(language);
    this.getTranslate(language);
  }
  public get languageSelectValue() {
    return this.languageSelectSubject.value;
  }

  getTranslate(region: string) {

    this.setTranslationListValue({
      loader: true,
      data: null,
      state: "waiting",
    });

    let obs = []

    if (region !== 'key-KEY')
      obs.push(this.http.get(`assets/i18n/json/${region}.json`));
    return forkJoin(obs).subscribe((results: any) => {
      this.setTranslationListValue({
        loader: false,
        data: results[1] ? results[1] : null,
        success: results[1] ? "completed" : "error",
        region: region,
        en: results[0]
      });
    })
  }
  removeduplicateKeyJson(old: any) {
    this.http.get(`assets/i18n/json/compare.json`).subscribe((compare: any) => {
      let newobj = { ...old, ...compare };
    });
  }

  missingKey() {
    let obs = []
    obs.push(this.http.get(`assets/i18n/json/en-GB.json`));
    obs.push(this.http.get(`assets/i18n/json/es-AR.json`));
    let objMissing: any = []
    forkJoin(obs).subscribe((results: any) => {
      Object.keys(results[0]).forEach((subKey) => {

        if (typeof results[0][subKey] === "object") {

          if (!results[1][subKey]) {
            objMissing.push({ [subKey]: results[0][subKey] })
          } else {

            let subObj: any = {}
            let check = false
            Object.keys(results[0][subKey]).forEach((subSubKey) => {
              if (!results[1][subKey][subSubKey]) {
                check = true
                subObj[subSubKey] = results[0][subKey][subSubKey]
              }
            })
            if (check)
              objMissing.push({ [subKey]: subObj })
          }

        } else {
          if (!results[1][subKey]) {

            objMissing.push({ [subKey]: results[0][subKey] })
          }
        }



      })

    })
  }

  compareTlaslate() {
    let obs = [];
    // obs.push(this.http.get(`assets/i18n/json/en-GB.json`));
    // obs.push(this.http.get(`assets/i18n/json/ar-SA.json`));
    // obs.push(this.http.get(`assets/i18n/json/de-DE.json`));
    // obs.push(this.http.get(`assets/i18n/json/es-ES.json`));
    // obs.push(this.http.get(`assets/i18n/json/fr-FR.json`));
    // obs.push(this.http.get(`assets/i18n/json/it-IT.json`));
    // obs.push(this.http.get(`assets/i18n/json/jp-JP.json`));
    // obs.push(this.http.get(`assets/i18n/json/nl-NL.json`));
    // obs.push(this.http.get(`assets/i18n/json/pl-PL.json`));
    // obs.push(this.http.get(`assets/i18n/json/pt-PT.json`));
    // obs.push(this.http.get(`assets/i18n/json/vi-VI.json`));
    // obs.push(this.http.get(`assets/i18n/json/zh-ZH.json`));
    obs.push(this.http.get(`assets/i18n/json/es-AR.json`));
    obs.push(this.http.get(`assets/i18n/json/pt-BR.json`));
    // let missingKeys_ar: any = {};
    // let missingKeys_de: any = {};
    // let missingKeys_es: any = {};
    // let missingKeys_fr: any = {};
    // let missingKeys_it: any = {};
    // let missingKeys_jp: any = {};
    // let missingKeys_nl: any = {};
    // let missingKeys_pl: any = {};
    // let missingKeys_pt: any = {};
    // let missingKeys_vt: any = {};
    // let missingKeys_zh: any = {};
    let missingKeys_ar: any = {};
    let missingKeys_pt_br: any = {};
    forkJoin(obs).subscribe((results: any) => {
      let fullKey = [];
      Object.keys(results[0]).forEach((key) => {
        this.setKey(results[1], results[0], key, missingKeys_ar);
        // missingKeys_de = this.setKey(results[2], results[0], key, missingKeys_de);
        // missingKeys_es = this.setKey(results[3], results[0], key, missingKeys_es);
        // missingKeys_fr = this.setKey(results[4], results[0], key, missingKeys_fr);
        // missingKeys_it = this.setKey(results[5], results[0], key, missingKeys_it);
        // missingKeys_jp = this.setKey(results[6], results[0], key, missingKeys_jp);
        // missingKeys_nl = this.setKey(results[7], results[0], key, missingKeys_nl);
        // missingKeys_pl = this.setKey(results[8], results[0], key, missingKeys_pl);
        // missingKeys_pt = this.setKey(results[9], results[0], key, missingKeys_pt);
        // missingKeys_vt = this.setKey(results[10], results[0], key, missingKeys_vt);
        // missingKeys_zh = this.setKey(results[11], results[0], key, missingKeys_zh);
        missingKeys_ar = this.setKey(results[0], results[0], key, missingKeys_ar);
        missingKeys_pt_br = this.setKey(results[1], results[0], key, missingKeys_pt_br);
      });
      // fullKey.push({ language: "ar-SA", object: missingKeys_ar });
      // fullKey.push({ language: "de-DE", object: missingKeys_de });
      // fullKey.push({ language: "es-ES", object: missingKeys_es });
      // fullKey.push({ language: "fr-FR", object: missingKeys_fr });
      // fullKey.push({ language: "it-IT", object: missingKeys_it });
      // fullKey.push({ language: "jp-JP", object: missingKeys_jp });
      // fullKey.push({ language: "nl-NL", object: missingKeys_nl });
      // fullKey.push({ language: "pl-PL", object: missingKeys_pl });
      // fullKey.push({ language: "pt-PT", object: missingKeys_pt });
      // fullKey.push({ language: "vi-VI", object: missingKeys_vt });
      // fullKey.push({ language: "zh-ZH", object: missingKeys_zh });
      fullKey.push({ language: "es-AR", object: missingKeys_ar });
      fullKey.push({ language: "pt-BR", object: missingKeys_pt_br });

    });

  }

  private setKey(jsonToCompare: any, jsonStarted: any, key: string, missingKeys: any) {
    if (jsonToCompare[key]) {
      if (typeof jsonStarted[key] === "object") {
        Object.keys(jsonStarted[key]).forEach((subKey) => {
          if (jsonToCompare[key][subKey]) {
            if (jsonToCompare[key][subKey] === jsonStarted[key][subKey]) {
              if (!missingKeys[key]) missingKeys[key] = {};
              missingKeys[key][subKey] = jsonStarted[key][subKey];
            }
          } else {
            if (!missingKeys[key]) missingKeys[key] = {};
            missingKeys[key][subKey] = "";
          }
        });
      } else {
        if (jsonToCompare[key] === jsonStarted[key]) {
          missingKeys[key] = jsonStarted[key];
        }
      }
    } else {
      missingKeys[key] = typeof jsonStarted[key] === "object" ? jsonStarted[key] : "";
    }
    return missingKeys;
  }

  resetsSubject() {
    this.setTranslationListValue(null);
  }
}
